import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ShoppingCart, CreditCard, Loader2, AlertCircle, Banknote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useToast } from '../common/Toast';
import { clearStoredSelections } from '../../hooks/useSuggestedCourses';
import { TermsModal, CoursePoliciesModal, ZelleModal, CashModal, AnimatedPrice } from './paymentModals';

// Animated count badge - uses key to trigger CSS animation on count change
function AnimatedBadge({ count }) {
  if (count === 0) return null;

  return (
    <span
      key={count}
      className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-[#589DD5] text-white text-xs font-bold rounded-full flex items-center justify-center animate-badge-pop"
    >
      {count}
    </span>
  );
}

export default function ExpandableCartCard({ selectedCoursesDetails, totalFees, studentId, onPaymentSuccess }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [hasAgreedToTermsOnce, setHasAgreedToTermsOnce] = useState(false);
  const [agreedToCoursePolicy, setAgreedToCoursePolicy] = useState(false);
  const [hasAgreedToCoursePolicyOnce, setHasAgreedToCoursePolicyOnce] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCoursePoliciesModal, setShowCoursePoliciesModal] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);

  // Track courses that already have Zelle payment submitted
  const [zelleSubmittedIds, setZelleSubmittedIds] = useState(new Set());

  // Initialize from API response - courses with zelle_payment_submitted_at
  useEffect(() => {
    const submittedIds = new Set(
      selectedCoursesDetails
        .filter(c => c.zelle_payment_submitted_at)
        .map(c => c.student_course_id)
    );
    setZelleSubmittedIds(submittedIds);
  }, [selectedCoursesDetails]);

  // Check if all selected courses already have Zelle payment submitted
  const allCoursesAlreadySubmitted = selectedCoursesDetails.length > 0 &&
    selectedCoursesDetails.every(c => zelleSubmittedIds.has(c.student_course_id));

  // Animation state - consolidated to prevent flickering
  const [animState, setAnimState] = useState({
    newIds: new Set(),
    exitingCourses: [],
    isOpen: false,
    displayCount: 0,
  });
  const prevCoursesRef = useRef([]);
  const prevIdsRef = useRef(new Set());
  const timeoutsRef = useRef([]);

  const itemCount = selectedCoursesDetails.length;

  // Clear timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => timeouts.forEach(t => clearTimeout(t));
  }, []);

  // Single consolidated effect for all animation state
  // useLayoutEffect runs synchronously before paint, preventing flash of unstyled content
  useLayoutEffect(() => {
    const prevIds = prevIdsRef.current;
    const prevCourses = prevCoursesRef.current;
    const currentIds = new Set(selectedCoursesDetails.map(c => c.student_course_id));

    // Find newly added courses
    const addedIds = new Set();
    currentIds.forEach(id => {
      if (!prevIds.has(id)) {
        addedIds.add(id);
      }
    });

    // Find removed courses
    const removedCourses = prevCourses.filter(c => !currentIds.has(c.student_course_id));
    const coursesWithPosition = removedCourses.map(course => {
      const originalIdx = prevCourses.findIndex(c => c.student_course_id === course.student_course_id);
      return { ...course, originalIdx };
    });

    // Update all state at once to prevent flickering
    // When going to 0 items, collapse immediately (don't wait for exit animation)
    // This creates one unified downward motion instead of two separate bumps
    setAnimState(prev => {
      const newExiting = [...prev.exitingCourses, ...coursesWithPosition];
      return {
        newIds: addedIds.size > 0 ? addedIds : prev.newIds,
        exitingCourses: newExiting,
        isOpen: itemCount > 0,
        displayCount: itemCount > 0 ? itemCount : prev.displayCount,
      };
    });

    // Clear new IDs after animation
    if (addedIds.size > 0) {
      const t1 = setTimeout(() => {
        setAnimState(prev => ({ ...prev, newIds: new Set() }));
      }, 300);
      timeoutsRef.current.push(t1);
    }

    // Clear exiting courses after animation completes
    if (removedCourses.length > 0) {
      const removedIds = new Set(removedCourses.map(c => c.student_course_id));
      const t2 = setTimeout(() => {
        setAnimState(prev => {
          const remaining = prev.exitingCourses.filter(c => !removedIds.has(c.student_course_id));
          return {
            ...prev,
            exitingCourses: remaining,
            // isOpen is already determined by itemCount, just clean up exiting courses
          };
        });
      }, 220);
      timeoutsRef.current.push(t2);
    }

    prevIdsRef.current = currentIds;
    prevCoursesRef.current = [...selectedCoursesDetails];
  }, [selectedCoursesDetails, itemCount]);

  // Destructure for easier access
  const { newIds: newCourseIds, exitingCourses, isOpen, displayCount } = animState;

  // Both agreements required for payment
  const canProceed = agreedToTerms && agreedToCoursePolicy;

  const handleCheckout = async () => {
    if (itemCount === 0 || totalFees.total === 0 || !canProceed) return;

    setLoading(true);
    setError(null);

    try {
      const studentCourseIds = selectedCoursesDetails.map(c => c.student_course_id);
      const { data } = await api.post(
        `/parent/children/${studentId}/payments/create-checkout-session`,
        { studentCourseIds }
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleOfflinePayment = async (paymentMethod) => {
    if (itemCount === 0 || totalFees.total === 0 || !canProceed) return;

    setOfflineLoading(true);
    setError(null);

    try {
      const studentCourseIds = selectedCoursesDetails.map(c => c.student_course_id);
      const { data } = await api.post(
        `/parent/children/${studentId}/payments/offline-payment-request`,
        { studentCourseIds, paymentMethod }
      );

      // Track submitted Zelle payments to prevent duplicate submissions
      if (paymentMethod === 'zelle') {
        const allSubmitted = [...(data.alreadySubmitted || []), ...(data.newlySubmitted || [])];
        if (allSubmitted.length > 0) {
          setZelleSubmittedIds(prev => {
            const newSet = new Set(prev);
            allSubmitted.forEach(id => newSet.add(id));
            return newSet;
          });
        }
      }

      // Close modals
      setShowZelleModal(false);
      setShowCashModal(false);

      // Clear cart
      clearStoredSelections(studentId);

      // Show success toast
      toast.success('Payment request submitted! Our office will verify your payment and update your enrollment status.', {
        duration: 7000,
      });

      // Trigger refetch
      if (onPaymentSuccess) {
        onPaymentSuccess();
      }
    } catch (err) {
      console.error('Offline payment error:', err);
      setError(err.response?.data?.error || 'Failed to submit payment request. Please try again.');
    } finally {
      setOfflineLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isOpen ? 'bg-[#589DD5]/10 text-[#589DD5]' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
            }`}>
              <ShoppingCart className="w-5 h-5" />
            </div>
            <AnimatedBadge count={isOpen ? displayCount : 0} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              {isOpen ? t('checkout.title') : t('checkout.selectCourses')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isOpen
                ? t('checkout.coursesSelected', { count: displayCount })
                : t('checkout.chooseCourses')
              }
            </p>
          </div>
        </div>

        {/* Content - uses CSS grid for smooth height animation */}
        <div
          className="grid transition-all duration-300 ease-out"
          style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div className="px-4 pb-4">
              {/* Selected Courses List */}
              <div className="py-3 space-y-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('checkout.selectedCourses')}
                </p>
                {/* Scrollable container for many courses */}
                <div className="space-y-0 max-h-48 overflow-y-auto cart-scrollbar">
                  {(() => {
                    // Build display list maintaining original positions for exiting items
                    const displayItems = [];

                    // Add current courses with offset to sort after exiting items at same position
                    selectedCoursesDetails.forEach((course, idx) => {
                      displayItems.push({
                        ...course,
                        isExiting: false,
                        sortIdx: idx + 0.5,
                      });
                    });

                    // Add exiting courses with their original position (integer)
                    exitingCourses.forEach(course => {
                      displayItems.push({
                        ...course,
                        isExiting: true,
                        sortIdx: course.originalIdx,
                      });
                    });

                    // Sort by position
                    displayItems.sort((a, b) => a.sortIdx - b.sortIdx);

                    return displayItems.map((course) => {
                      const courseFee = (parseFloat(course.tuition_fee) || 0) +
                                       (parseFloat(course.textbook_fee) || 0) +
                                       (parseFloat(course.exam_fee) || 0);
                      const isNew = newCourseIds.has(course.student_course_id);
                      const isAnimating = isNew || course.isExiting;
                      return (
                        <div
                          key={course.student_course_id}
                          className={`flex justify-between items-center border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                            isAnimating ? '' : 'py-1.5'
                          } ${isNew ? 'animate-slide-in-right' : ''
                          } ${course.isExiting ? 'animate-slide-out-right pointer-events-none' : ''}`}
                        >
                          <span className="text-sm text-gray-600 dark:text-gray-400 leading-tight truncate flex-1 min-w-0">{course.course_title}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white shrink-0 ml-2">${courseFee.toFixed(2)}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Fee Breakdown */}
              <div className="py-3 space-y-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('checkout.feeBreakdown')}
                </p>
                <div className="space-y-1 text-sm">
                  {totalFees.tuition > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('checkout.tuition')}</span>
                      <span className="font-medium dark:text-white">${totalFees.tuition.toFixed(2)}</span>
                    </div>
                  )}
                  {totalFees.textbook > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('checkout.textbooks')}</span>
                      <span className="font-medium dark:text-white">${totalFees.textbook.toFixed(2)}</span>
                    </div>
                  )}
                  {totalFees.exam > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('checkout.examFees')}</span>
                      <span className="font-medium dark:text-white">${totalFees.exam.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Totals */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mb-3 space-y-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  {t('checkout.paymentTotals')}
                </p>
                {/* Cash/Zelle */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('checkout.cashZelle')}</span>
                  <AnimatedPrice
                    value={totalFees.total}
                    height={20}
                    className="text-sm font-semibold text-gray-900 dark:text-white"
                  />
                </div>
                {/* Card (US) - 4% fee */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('checkout.cardUS')} <span className="text-xs text-gray-400 dark:text-gray-500">+4%</span>
                  </span>
                  <AnimatedPrice
                    value={totalFees.total * 1.04}
                    height={20}
                    className="text-sm font-semibold text-gray-900 dark:text-white"
                  />
                </div>
                {/* Card (KR) - 4.5% fee */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {t('checkout.cardKR')} <span className="text-xs text-gray-400 dark:text-gray-500">+4.5%</span>
                  </span>
                  <AnimatedPrice
                    value={totalFees.total * 1.045}
                    height={20}
                    className="text-sm font-semibold text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Terms Agreement */}
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center shrink-0 cursor-pointer ${
                    agreedToTerms
                      ? 'bg-[#589DD5] border-[#589DD5]'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                  }`}
                  onClick={() => {
                    if (hasAgreedToTermsOnce) {
                      setAgreedToTerms(!agreedToTerms);
                    } else {
                      setShowTermsModal(true);
                    }
                  }}
                >
                  <svg
                    className={`w-3 h-3 text-white transition-opacity ${agreedToTerms ? 'opacity-100' : 'opacity-0'}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" className={agreedToTerms ? 'animate-checkmark' : ''} />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('checkout.agreeToTerms')}{' '}
                  <span
                    className="text-[#589DD5] hover:text-[#4a8bc4] underline cursor-pointer"
                    onClick={() => setShowTermsModal(true)}
                  >
                    {t('checkout.termsPolicy')}
                  </span>
                </span>
              </div>

              {/* Course Policy Agreement */}
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center shrink-0 cursor-pointer ${
                    agreedToCoursePolicy
                      ? 'bg-[#589DD5] border-[#589DD5]'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                  }`}
                  onClick={() => {
                    if (hasAgreedToCoursePolicyOnce) {
                      setAgreedToCoursePolicy(!agreedToCoursePolicy);
                    } else {
                      setShowCoursePoliciesModal(true);
                    }
                  }}
                >
                  <svg
                    className={`w-3 h-3 text-white transition-opacity ${agreedToCoursePolicy ? 'opacity-100' : 'opacity-0'}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" className={agreedToCoursePolicy ? 'animate-checkmark' : ''} />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('checkout.agreeToCoursePolicy')}{' '}
                  <span
                    className="text-[#589DD5] hover:text-[#4a8bc4] underline cursor-pointer"
                    onClick={() => setShowCoursePoliciesModal(true)}
                  >
                    {t('checkout.courseRefundPolicy')}
                  </span>
                </span>
              </div>

              {/* Payment Buttons */}
              <div className="space-y-2">
                {/* Pay with Cash */}
                <button
                  onClick={() => setShowCashModal(true)}
                  disabled={loading || offlineLoading || totalFees.total === 0 || !canProceed}
                  className="w-full py-3 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Banknote className="w-4 h-4" />
                  {t('checkout.payWithCash')}
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <span className="text-xs text-gray-400 dark:text-gray-500">{t('checkout.or')}</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>

                {/* Pay with Zelle */}
                <button
                  onClick={() => setShowZelleModal(true)}
                  disabled={loading || offlineLoading || totalFees.total === 0 || !canProceed}
                  className="w-full py-3 px-4 bg-[#6d1ed4] hover:bg-[#5a18b0] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {t('checkout.payWith')}
                  <img src="/assets/zelle-logo.svg" alt="Zelle" className="h-4 brightness-0 invert" />
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <span className="text-xs text-gray-400 dark:text-gray-500">{t('checkout.or')}</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>

                {/* Pay with Card */}
                <button
                  onClick={handleCheckout}
                  disabled={loading || offlineLoading || totalFees.total === 0 || !canProceed}
                  className="w-full py-3 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('checkout.processing')}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      {t('checkout.payWithCard')}
                    </>
                  )}
                </button>
              </div>

              {/* Stripe Badge */}
              <a
                href="https://stripe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 mt-3 opacity-60 hover:opacity-80 transition-opacity"
                aria-label="Powered by Stripe"
              >
                <span className="text-xs text-gray-400 dark:text-gray-500">{t('checkout.poweredBy')}</span>
                <picture className="dark:hidden">
                  <source srcSet="/assets/stripe-wordmark.svg" type="image/svg+xml" />
                  <img src="/assets/stripe-wordmark-small.png" alt="Stripe" className="h-5" />
                </picture>
                <picture className="hidden dark:block">
                  <source srcSet="/assets/stripe-wordmark-white.svg" type="image/svg+xml" />
                  <img src="/assets/stripe-wordmark-white-small.png" alt="Stripe" className="h-5" />
                </picture>
              </a>
            </div>
          </div>
        </div>
      </div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => {
          setAgreedToTerms(true);
          setHasAgreedToTermsOnce(true);
          setShowTermsModal(false);
        }}
        onDisagree={() => {
          setAgreedToTerms(false);
          setShowTermsModal(false);
        }}
      />

      <CoursePoliciesModal
        isOpen={showCoursePoliciesModal}
        courses={selectedCoursesDetails}
        onClose={() => {
          setAgreedToCoursePolicy(true);
          setHasAgreedToCoursePolicyOnce(true);
          setShowCoursePoliciesModal(false);
        }}
        onDisagree={() => {
          setAgreedToCoursePolicy(false);
          setShowCoursePoliciesModal(false);
        }}
      />

      <ZelleModal
        isOpen={showZelleModal}
        onClose={() => setShowZelleModal(false)}
        onConfirm={() => handleOfflinePayment('zelle')}
        totalAmount={totalFees.total}
        isLoading={offlineLoading}
        studentId={studentId}
        studentCourseIds={selectedCoursesDetails.map(c => c.student_course_id)}
        allCoursesAlreadySubmitted={allCoursesAlreadySubmitted}
      />

      <CashModal
        isOpen={showCashModal}
        onClose={() => setShowCashModal(false)}
        onConfirm={() => handleOfflinePayment('cash')}
        totalAmount={totalFees.total}
        isLoading={offlineLoading}
      />
    </>
  );
}
